import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { DROPDOWN_FETCH_PAGE_SIZE, getCurrencySymbol, usePermissions } from '@klareospace/pharos-lib-front-utils';
import { isDefined } from '@klareospace/pharos-lib-utils';
import {
  CURRENCIES,
  HANDLING_UNIT_TEMPERATURES,
  VOLUME_UNITS,
  WEIGHT_UNITS,
  type HandlingUnitType,
} from '@klareospace/pharos-lib-types';
import {
  Button,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  ToggleGroup,
  ToggleGroupItem,
} from '@klareospace/pharos-lib-web-utils';

import { useHandlingUnitTypes } from '&/features/settings/handling-unit-types';
import { useTenantDefaults } from '&/features/settings/tenant-defaults';
import { TagsInput } from '&/shared/components';

import type { ContentHandlingUnitLineFormInput, OrderCreationFormInput } from '../../schemas/order-creation.schema';
import { HandlingUnitLineCard } from './handling-unit-line-card';

// ─── Constants ────────────────────────────────────────────────────────────────

const QUICK_QUANTITIES = [1, 2, 3, 4, 5];

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * "Content" section body — total quantity, weight/volume/order value, a manual handling-unit
 * list, and tags. No scanning UI (reserved for a later release): handling units are added by
 * picking a type and clicking "+ Add".
 */
export function ContentSection(): React.JSX.Element {
  const { i18n, t: tCommon } = useTranslation('common');
  const { t } = useTranslation('order-creation');
  const { control, formState, setValue } = useFormContext<OrderCreationFormInput>();
  const { hasPermission } = usePermissions();
  const incompleteMessage = formState.errors.content?.message;

  const { data: handlingUnitTypesData } = useHandlingUnitTypes({
    enabled: hasPermission('handling-unit-types:list'),
    page: 1,
    pageSize: DROPDOWN_FETCH_PAGE_SIZE,
    statuses: ['active'],
  });
  const handlingUnitTypes = useMemo(() => handlingUnitTypesData?.items ?? [], [handlingUnitTypesData]);
  const handlingUnitTypesByCode = useMemo(
    () => new Map(handlingUnitTypes.map((type) => [type.code, type])),
    [handlingUnitTypes],
  );

  const { data: tenantDefaults } = useTenantDefaults({ enabled: hasPermission('tenant-defaults:read') });
  const defaultTypeCode = useMemo(
    () => resolveDefaultTypeCode(handlingUnitTypes, tenantDefaults?.defaultHandlingUnitTypeId ?? null),
    [handlingUnitTypes, tenantDefaults],
  );
  const [pendingTypeCode, setPendingTypeCode] = useState('');
  useEffect(() => {
    if (!pendingTypeCode && defaultTypeCode) setPendingTypeCode(defaultTypeCode);
  }, [defaultTypeCode, pendingTypeCode]);

  const { append, fields, remove } = useFieldArray({ control, name: 'content.handlingUnits' });
  const watchedLines = useWatch({ control, name: 'content.handlingUnits' }) ?? [];
  const tags = useWatch({ control, name: 'content.tags' }) ?? [];
  const totalUnits = watchedLines.reduce((sum, line) => sum + line.quantity, 0);

  function handleAddHandlingUnit(): void {
    if (!pendingTypeCode) return;
    const existingIndex = watchedLines.findIndex((line) => line.typeCode === pendingTypeCode && !line.barcode);
    if (existingIndex >= 0) {
      setValue(`content.handlingUnits.${existingIndex}.quantity`, watchedLines[existingIndex].quantity + 1);
      return;
    }
    append({ barcode: '', quantity: 1, temperature: HANDLING_UNIT_TEMPERATURES.DRY, typeCode: pendingTypeCode });
  }

  // `setValue` on the nested path, not the field array's `update()` — `update()` re-inserts the
  // row under a new field id, remounting its inputs and dropping focus mid-keystroke (barcode).
  function handleLineChange(index: number, patch: Partial<ContentHandlingUnitLineFormInput>): void {
    const options = { shouldDirty: true, shouldValidate: true };
    if (isDefined(patch.barcode)) setValue(`content.handlingUnits.${index}.barcode`, patch.barcode, options);
    if (isDefined(patch.quantity)) setValue(`content.handlingUnits.${index}.quantity`, patch.quantity, options);
    if (isDefined(patch.temperature)) {
      setValue(`content.handlingUnits.${index}.temperature`, patch.temperature, options);
    }
  }

  return (
    <div className="space-y-4">
      {incompleteMessage && <p className="text-sm text-destructive">{incompleteMessage}</p>}
      <FormField
        control={control}
        name="content.totalQuantity"
        render={({ field }): React.JSX.Element => (
          <FormItem className="space-y-2">
            <FormLabel>{t('sections.content.fields.totalQuantity.label')}</FormLabel>
            <div className="flex flex-wrap items-center gap-2">
              <ToggleGroup
                onValueChange={(nextValue): void => {
                  if (!nextValue) return;
                  field.onChange(nextValue);
                }}
                type="single"
                value={resolveQuickQuantityValue(field.value)}
                variant="outline"
              >
                {QUICK_QUANTITIES.map((quantity) => (
                  <ToggleGroupItem
                    className="h-8 min-w-[40px] border-border bg-background px-3 text-foreground hover:bg-muted data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-primary"
                    key={quantity}
                    value={String(quantity)}
                  >
                    {quantity}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground">{tCommon('dateRangeFilter.presets.custom')}</span>
                <FormControl>
                  <Input {...field} className="h-8 w-20 text-sm" min={1} type="number" />
                </FormControl>
              </div>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-6">
        <FormField
          control={control}
          name="content.totalWeight"
          render={({ field }): React.JSX.Element => (
            <FormItem>
              <FormLabel>{t('sections.content.fields.totalWeight.label')}</FormLabel>
              <div className="flex gap-2">
                <FormControl>
                  <Input
                    {...field}
                    className="flex-1"
                    min={0}
                    placeholder={t('sections.content.fields.totalWeight.placeholder')}
                    step="0.1"
                    type="number"
                  />
                </FormControl>
                <FormField
                  control={control}
                  name="content.weightUnit"
                  render={({ field: unitField }): React.JSX.Element => (
                    <Select onValueChange={unitField.onChange} value={unitField.value}>
                      <SelectTrigger aria-label={tCommon('fields.weightUnit')} className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.values(WEIGHT_UNITS).map((unit) => (
                          <SelectItem key={unit} value={unit}>
                            {tCommon(`units.${unit}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="content.totalVolume"
          render={({ field }): React.JSX.Element => (
            <FormItem>
              <FormLabel>{t('sections.content.fields.totalVolume.label')}</FormLabel>
              <div className="flex gap-2">
                <FormControl>
                  <Input
                    {...field}
                    className="flex-1"
                    min={0}
                    placeholder={t('sections.content.fields.totalVolume.placeholder')}
                    step="0.1"
                    type="number"
                  />
                </FormControl>
                <FormField
                  control={control}
                  name="content.volumeUnit"
                  render={({ field: unitField }): React.JSX.Element => (
                    <Select onValueChange={unitField.onChange} value={unitField.value}>
                      <SelectTrigger aria-label={tCommon('fields.volumeUnit')} className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.values(VOLUME_UNITS).map((unit) => (
                          <SelectItem key={unit} value={unit}>
                            {tCommon(`units.${unit}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="content.orderValue"
          render={({ field }): React.JSX.Element => (
            <FormItem>
              <FormLabel>{t('common.orderValue')}</FormLabel>
              <div className="flex gap-2">
                <FormControl>
                  <Input
                    {...field}
                    className="flex-1"
                    min={0}
                    placeholder={t('sections.content.fields.orderValue.placeholder')}
                    step="0.01"
                    type="number"
                  />
                </FormControl>
                <FormField
                  control={control}
                  name="content.currency"
                  render={({ field: currencyField }): React.JSX.Element => (
                    <Select onValueChange={currencyField.onChange} value={currencyField.value}>
                      <SelectTrigger
                        aria-label={t('sections.content.fields.orderValue.currencyLabel')}
                        className="w-20"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CURRENCIES.map((currency) => (
                          <SelectItem key={currency} value={currency}>
                            {getCurrencySymbol(currency, i18n.language)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="space-y-3">
        <Label>{tCommon('fields.handlingUnits')}</Label>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Select onValueChange={setPendingTypeCode} value={pendingTypeCode}>
            <SelectTrigger
              aria-label={t('sections.content.fields.handlingUnits.typeSelectAriaLabel')}
              className="h-9 min-w-0 flex-1 text-sm sm:min-w-[200px]"
            >
              <SelectValue placeholder={t('sections.content.fields.handlingUnits.selectPlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              {handlingUnitTypes.map((type) => (
                <SelectItem key={type.id} value={type.code}>
                  {type.label} · {type.code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button className="h-9 shrink-0 gap-1.5" onClick={handleAddHandlingUnit} type="button" variant="outline">
            <Plus aria-hidden="true" className="h-3.5 w-3.5" />
            {t('sections.content.fields.handlingUnits.add')}
          </Button>
        </div>

        {fields.length === 0 ? (
          <p className="rounded-lg border border-dashed bg-muted/30 p-4 text-center text-xs text-muted-foreground">
            {t('sections.content.fields.handlingUnits.empty')}
          </p>
        ) : (
          <div className="space-y-2">
            {fields.map((field, index) => {
              const line = watchedLines[index];
              if (!line) return null;
              return (
                <HandlingUnitLineCard
                  handlingUnitType={handlingUnitTypesByCode.get(line.typeCode)}
                  key={field.id}
                  line={line}
                  onChange={(patch): void => handleLineChange(index, patch)}
                  onRemove={(): void => remove(index)}
                />
              );
            })}
            <p className="text-xs text-muted-foreground">
              {t('sections.content.fields.handlingUnits.footerLines', { count: fields.length })}
              {' · '}
              {t('sections.content.fields.handlingUnits.footerUnits', { count: totalUnits })}
            </p>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label>{t('common.tags')}</Label>
        <TagsInput
          ariaLabel={t('common.tags')}
          onChange={(nextTags): void => setValue('content.tags', nextTags, { shouldDirty: true, shouldValidate: true })}
          placeholder={t('sections.content.fields.tags.placeholder')}
          removeTagAriaLabel={(tag): string => t('sections.content.fields.tags.remove', { tag })}
          value={tags}
        />
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Tenant-default handling-unit type (mapped id → code), falling back to the first active type. */
function resolveDefaultTypeCode(types: HandlingUnitType[], defaultTypeId: string | null): string {
  const matched = defaultTypeId ? types.find((type) => type.id === defaultTypeId) : undefined;
  return matched?.code ?? types[0]?.code ?? '';
}

/** Normalizes `rawValue` (e.g. '3.0', '03') so any representation of a quick quantity highlights its pill. */
function resolveQuickQuantityValue(rawValue: string): string {
  const parsed = Number(rawValue);
  return QUICK_QUANTITIES.includes(parsed) ? String(parsed) : '';
}
